import { AfterViewInit, Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as GC from '@grapecity/spread-sheets';
import { CanDeactivateGuard } from '@guards/can-deactivate/can-deactivate-guard.service';
import { clone, isEmpty } from '@helpers/arrays';
import { checkDropdownValues } from '@helpers/spread/sheet-functions';
import { ILookupSetup } from '@interfaces/lookup-setup';
import { DialogSaveObject } from '@models/dialog-save-object.d';
import { HoverNotification } from '@models/hover-notification';
import { PermissionsService } from '@services/auth/permissions.service';
import { TableSetupService } from '@services/lookup-tables/table-setup.service';
import { MenuService } from '@services/menu/menu.service';
import { MultiNotificationsService } from '@services/multi-notifications/multi-notifications.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import { bindSpreadJSEventsToSheet, mapActionKeys } from '@shared/helpers/spread/map-action-keys';
import { setDefaultBookOptions, setDefaultSheetOptions } from '@shared/spreadsheet/base';
import { SpreadContextMenu } from '@shared/spreadsheet/protection';
import { Toast } from 'ngx-toastr';
import { forkJoin, fromEvent, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  DATATYPE_COL_POS,
  DEFAULT_DATATABLE_TYPES,
  DEFAULT_DIMENSION_DEFINITION,
  DEFAULT_FIELDS,
  DEFAULT_INTERPOLATION_VALUES,
  DEFINITION_COL_POS,
  DIM1_GROUP,
  DIM2_GROUP,
  DIM3_GROUP,
  DimensionDefinitionItem,
  DIMENSION_COL_POS,
  INTERPOLATION_COL_POS,
  MINIMUM_ROWS,
  PADDING_ROWS,
} from './helpers/constants';

import { clipboardClearOnCut } from '@helpers/clipboard-events';
import { spreadsheetConfig } from '@shared/spreadsheet/spreadsheet-config';
import { disableEmptyDimensionsCells } from './helpers/disable-empty-dimensions-cells';
import { onMouseMove } from './helpers/on-mouse-move';
import { setupHeaders } from './helpers/setup-headers';
import { addValidationToRow, removeValidationFromRow } from './helpers/validators';
import { isEmpty as _isEmpty } from 'lodash-es';
import { ToastLevel } from '@shared/toasterSetup';

@Component({
  selector: 'app-lookup-setup',
  templateUrl: './lookup-setup.component.html',
  styleUrls: ['./lookup-setup.component.scss'],
})
export class LookupSetupComponent implements OnInit, OnDestroy, CanDeactivateGuard, AfterViewInit {
  @ViewChild('spreadContainer', { static: false }) spreadContainer;
  @ViewChild('spreadDiv', { static: false }) spreadDiv;
  public spreadBackColor = spreadsheetConfig.backColor;
  public hostStyle = spreadsheetConfig.hostStyle;
  private sheet: GC.Spread.Sheets.Worksheet = null;
  private spread: GC.Spread.Sheets.Workbook;
  private lookupTableSetup: ILookupSetup[];
  public isLoading = true;
  public isSaving = false;
  public isSheetValid = false;
  private _saveEnabled = false;
  private willCauseDataLoss = false;
  private datatableTypes = DEFAULT_DATATABLE_TYPES;
  private interpolation = DEFAULT_INTERPOLATION_VALUES;
  private dimensionDefinition = DEFAULT_DIMENSION_DEFINITION;
  public changes = [];
  public dirtyRows: any[] = [];
  private changeTimeout: NodeJS.Timer = null;
  private subscriptions: Subscription[] = [];
  private destroyed$ = new Subject();
  private userRoleCanEdit;

  public notification: HoverNotification = {};
  private toast: Toast = { title: '', message: 'Lookup tables successfully updated' } as Toast;

  constructor(
    private menu: MenuService,
    private zone: NgZone,
    private tableSetupService: TableSetupService,
    public multiNotifications: MultiNotificationsService,
    public notifications: NotificationsService,
    private permissions: PermissionsService
  ) {}

  ngOnInit() {
    this.userRoleCanEdit = this.permissions.getPermission('ModelStructureLookupTables') === 'W';
  }

  ngOnDestroy() {
    this.notifications.hideToasts();
    this.destroyed$.next();
  }

  ngAfterViewInit() {
    fromEvent(this.spreadContainer.nativeElement, 'mousemove')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((e: any) => {
        this.notification = onMouseMove(this.sheet, e.layerX, e.layerY);
      });

    // Dialog Save Button changes
    this.menu.dialogSaveButton$
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged(), debounceTime(50))
      .subscribe((val: DialogSaveObject) => {
        // Only respond to click event if was triggered for this page
        if (val.page === 'lookup-setup') {
          // Discard lost-of-data changes functionality
          if (val.source === `${val.page}-discard`) {
            this.menu.dialogSaveButton = { ...this.menu.dialogSaveButton, source: `${val.page}-discarded` };
            this.discardChanges();
          } else if (val.clicked && val.enabled) {
            this.saveData();
            this.menu.dialogSaveButton = {
              page: val.page,
              enabled: false,
              valid: this.isValidTable(),
              clicked: false,
              source: `${val.page} save`,
            };
          }
        }
      });

    this.menu.deactivatePopupClosed$.pipe(takeUntil(this.destroyed$)).subscribe(popupResult => {
      if (popupResult === false) {
        this.spread.focus();
      }
    });

    if (this.spread) {
      this.spread.focus();
    }
  }

  getTableSetup(): any {
    this.isLoading = true;
    this.willCauseDataLoss = false;
    forkJoin([this.tableSetupService.getLookupTableGlobalParams(), this.tableSetupService.getFilteredLookupTables()])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([lookupTableParams, lookupTableDefinition]) => {
        if (this.sheet) {
          this.processParams(lookupTableParams);
          this.lookupTableSetup = this.processTables(lookupTableDefinition);
          this.isSheetValid = false;
          try {
            this.setupDataBinding();
            setupHeaders(this.sheet);
          } catch (error) {
          } finally {
            this.isLoading = false;
          }
        }
      });
  }

  processTables(lookupTableDefinition): ILookupSetup[] {
    const balanceSheetLookupTables = [];
    const userDefinedLookupTables = [];
    lookupTableDefinition.forEach(lookupTable => {
      if (['CommitDraw', 'BALANCESHEET'].includes(lookupTable.DataTableType)) {
        balanceSheetLookupTables.push(lookupTable);
      } else {
        userDefinedLookupTables.push(lookupTable);
      }
    });
    let sheet = this.spread.getSheetFromName('Validation');
    if (!sheet) {
      this.spread.addSheet(this.spread.getSheetCount());
      sheet = this.spread.getSheet(this.spread.getSheetCount() - 1);
      sheet.name('Validation');
      sheet.visible(false);
    }
    for (let i = 0; i < balanceSheetLookupTables.length; i++) {
      sheet.setValue(i, 0, balanceSheetLookupTables[i].Mnemonic);
    }

    return userDefinedLookupTables;
  }

  processParams(params: any) {
    if (params.Interpolation) {
      this.interpolation = params.Interpolation.map(x => x.Value);
    }
    if (params.DatatableType) {
      this.datatableTypes = params.DatatableType.map(x => x.Value);
    }
    if (params.DimensionDefinition) {
      this.dimensionDefinition = params.DimensionDefinition.map(x => ({
        name: x.Value,
        type: x.Name,
      }));
    }
  }

  /**
   * Spreadsheet init lifecycle
   * @param args startup parameters provided to SpreadJS for SpreadJS
   */
  workbookInit(args) {
    this.spread = args.spread;
    this.setupWorkBook();
  }

  private setupWorkBook() {
    this.sheet = this.spread.getActiveSheet();
    this.sheet.options.rowHeaderVisible = true;
    this.sheet.suspendPaint();

    // =========================
    //   General Sheet Options
    // =========================

    this.sheet.name('Lookup Tables');
    this.sheet.setColumnCount(19);
    this.sheet.defaults.rowHeight = 20;
    this.sheet.defaults.colWidth = 100;

    // Book and Sheet Options
    setDefaultBookOptions(this.spread.options);
    setDefaultSheetOptions(this.sheet.options);

    // Protection options
    if (this.userRoleCanEdit) {
      this.sheet.getRange(-1, 0, -1, this.sheet.getColumnCount()).locked(false);
      this.sheet.options.protectionOptions.allowSelectLockedCells = false;
      this.sheet.options.protectionOptions.allowSelectUnlockedCells = true;
      SpreadContextMenu.setCopyCutPaste(this.spread).enableDeleteRows(this.spread);
    } else {
      this.sheet.getRange(-1, 0, -1, this.sheet.getColumnCount()).locked(true);
      this.sheet.options.isProtected = true;
      this.sheet.options.protectionOptions.allowSelectLockedCells = true;
      SpreadContextMenu.setCopyCutPaste(this.spread);
    }

    // Context Menu
    this.spread.options.allowExtendPasteRange = true;

    // Setup databinding
    this.setSheetEventBindings();
    this.getTableSetup();

    this.sheet.resumePaint();
  }

  setupDataBinding() {
    this.sheet.suspendPaint();
    this.sheet.suspendCalcService();
    // this.sheet.suspendEvent();
    // autoGenerateColumns needs to be disabled to avoid issues when creating columns and the data source is missing data in some of them
    // this.sheet.autoGenerateColumns = true;
    this.sheet.setDataSource(this.lookupTableSetup);

    const header_size = 100;
    this.sheet.bindColumns([
      { name: 'Name', displayName: 'Name', size: 120 },
      { name: 'Mnemonic', displayName: 'Mnemonic', size: 120 },
      { name: 'ResultType', displayName: 'Result Type', size: 120 },
      { name: 'DataTableType', displayName: 'Datatable Type', size: 120 },
      { name: 'Description', displayName: 'Description', size: 120 },
      { name: 'Dimensions', displayName: 'Dimensions', size: 120 },
      { name: 'DimMnemonic1', displayName: 'Definition', size: 100 },
      { name: 'DataType1', displayName: 'DataType', size: 100 },
      {
        name: 'Interpolation1',
        displayName: 'Interpolation',
        size: 100,
      },
      { name: 'DimMnemonic2', displayName: 'Definition', size: 100 },
      { name: 'DataType2', displayName: 'DataType', size: 100 },
      {
        name: 'Interpolation2',
        displayName: 'Interpolation',
        size: 100,
      },
      { name: 'DimMnemonic3', displayName: 'Definition', size: 100 },
      { name: 'DataType3', displayName: 'DataType', size: 100 },
      {
        name: 'Interpolation3',
        displayName: 'Interpolation',
        size: 100,
      },
    ]);

    //this.autofitColumns();

    // Grey out the unusable Dimensions for prepopulated data
    if (this.lookupTableSetup && this.lookupTableSetup.length > 0 && this.lookupTableSetup[0].Mnemonic !== null) {
      this.isSheetValid = true;
      for (let r = 0; r < this.sheet.getRowCount() && this.lookupTableSetup; r++) {
        disableEmptyDimensionsCells(
          this.sheet,
          this.interpolation,
          this.dimensionDefinition,
          r,
          this.sheet.getCell(r, DIMENSION_COL_POS).value()
        );
        addValidationToRow(this.sheet, this.datatableTypes, r);
        this.isSheetValid = this.isSheetValid && this.isValidRow(r);
      }
    }

    this.sheet.clearPendingChanges();
    this.getChangesToRows();
    this.addBottomRowsIfNeeded();

    // this.sheet.resumeEvent();
    this.sheet.resumeCalcService();
    this.sheet.resumePaint();
  }

  autofitColumns() {
    // Autofit 5 first columns immediately, defer others to speed up function return
    // Try-catch blocks to avoid console errors when switching tabs quickly

    try {
      for (let col = 0; col < this.sheet.getColumnCount(); col++) {
        if (col < 5) {
          this.sheet.autoFitColumn(col);
        } else {
          setTimeout(() => {
            try {
              this.sheet.autoFitColumn(col);
            } catch {}
          });
        }
      }
    } catch (error) {}
  }

  // Adds PADDING_ROWS rows at the bottom to help the user start adding new Lookup Tables
  private addBottomRowsIfNeeded() {
    const count = this.sheet.getRowCount() - 1;
    if (!this.lookupTableSetup) {
      this.sheet.addRows(this.sheet.getRowCount(), MINIMUM_ROWS);
    } else {
      const paddingAmount = count > 50 ? PADDING_ROWS : MINIMUM_ROWS - this.sheet.getRowCount();
      for (let r = 0; r < paddingAmount; r++) {
        const index = count - r;

        if (this.lookupTableSetup[index] === undefined || JSON.stringify(this.lookupTableSetup[index]) !== JSON.stringify({})) {
          const rowCount = this.sheet.getRowCount();
          this.sheet.addRows(rowCount, 1);
        }
      }
    }
  }

  // On cell edit leave, on cell value change, on paste
  setSheetEventBindings() {
    mapActionKeys(this.spread, this.sheet, null, this.userRoleCanEdit, false);
    bindSpreadJSEventsToSheet(this.sheet);

    // Trigger on cell selection
    this.sheet.bind(GC.Spread.Sheets.Events.SelectionChanging, (event, info) => {
      const { row: r, col: c } = info.newSelections[0];
      const selectedStyle = this.sheet.getStyle(r, c);

      if (selectedStyle && selectedStyle.backColor === 'Background 2') {
        this.sheet.clearSelection();
        const { row, col, rowCount, colCount } = info.oldSelections[0];
        this.sheet.setSelection(row, col, rowCount, colCount);
      }
    });

    // Trigger on cell change
    this.sheet.bind(GC.Spread.Sheets.Events.CellChanged, (event, info) => {
      const { row, col, newValue, oldValue, propertyName } = info;

      this.sheet.suspendPaint();
      this.sheet.suspendEvent();
      this.sheet.suspendCalcService();

      // Filter events to only validate when value is modified
      if (propertyName === 'value' && !this.isLoading) {
        if (col === DIMENSION_COL_POS) {
          if (oldValue !== newValue && this.lookupTableSetup[row].Id) {
            this.willCauseDataLoss = true;
          }
          this.onNumberOfDimensionsChanged(row, newValue);
        }

        if (!isEmpty(newValue)) {
          this.setDefaultValuesForDataTypeCell(row, col);
          setTimeout(() => this.sheet.autoFitColumn(col));
          this.addBottomRowsIfNeeded();
        }
      }

      checkDropdownValues(this.sheet, row, col);

      this.checkSaveEnabled();

      // Delete keys with null, undefined or empty string values
      Object.keys(this.lookupTableSetup[info.row]).forEach(key => {
        if (isEmpty(this.lookupTableSetup[info.row][key])) {
          delete this.lookupTableSetup[info.row][key];
        }
      });

      if (this.isEmptyRow(this.lookupTableSetup[info.row])) {
        removeValidationFromRow(this.sheet, info.row);
      }

      this.sheet.resumeCalcService();
      this.sheet.resumeEvent();
      this.sheet.resumePaint();
    });

    // Trigger on pasting data (before)
    this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasting, (event, info) => {
      if (this.userRoleCanEdit) {
        const { row, col, rowCount, colCount } = info.cellRange;

        // if is a cut event, it clears the clipboard content once the event ends
        clipboardClearOnCut(info, this.sheet);

        const sheetRowCount = this.sheet.getRowCount();
        const newRowsNeeded = rowCount - (sheetRowCount - row) + PADDING_ROWS;
        if (newRowsNeeded > 0) {
          this.sheet.addRows(sheetRowCount, newRowsNeeded);
        }
      }
    });

    // Trigger on pasted data (after)
    this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasted, (event, info) => {
      if (this.userRoleCanEdit) {
        this.onClipboardPastedCallback(info);

        // Scroll down to the last non-empty row
        const { row, rowCount } = info.cellRange;
        this.sheet.showRow(row + rowCount, GC.Spread.Sheets.VerticalPosition.center);
      }
    });

    // Trigger on drag and drop values
    this.sheet.bind(GC.Spread.Sheets.Events.DragDropBlockCompleted, (event, info) => {
      const { toRow, rowCount } = info;
      for (let r = toRow; r < rowCount + toRow; r++) {
        this.updateValidationsAndFieldsAfterEvent(r);
      }
    });

    // Trigger on drag and fill values
    this.sheet.bind(GC.Spread.Sheets.Events.DragFillBlockCompleted, (event, info) => {
      this.onClipboardPastedCallback({ ...info, cellRange: info.fillRange }); // cellrange is called fillrange for dragfill event
    });

    // Trigger on changing rows
    this.sheet.bind(GC.Spread.Sheets.Events.RowChanged, (evt, args) => {
      // TODO - Bug 46134 - This is a temporary workaround for spreadJS version 15 addRow() copying formatting and validators. Should be able to be rolled back after Spread 16 upgrade.
      if (args.propertyName === 'addRows' || args.propertyName === 'deleteRows') {
        this.sheet.getRange(args.row, -1, args.rowCount, -1).validator(null);
      }

      // Handling deleting rows
      if (args.propertyName === 'deleteRows') {
        this.addBottomRowsIfNeeded();
        this.isSheetValid = this.isValidTable();
        this.sheet.setSelection(args.row, 0, 1, 1);
        this.checkSaveEnabled();
      }
    });

    const commandManager = this.spread.commandManager();
    commandManager.register('onUndo', (e, info, aaa) => {
      setTimeout(() => {
        this.lookupTableSetup.forEach((row, index) => {
          if (this.isEmptyRow(row)) {
            removeValidationFromRow(this.sheet, index);
          }
        });
      });
    });
    commandManager.setShortcutKey('onUndo', GC.Spread.Commands.Key.z, true, false, false, false);

    commandManager.register('deleteCell', (context, info) => {
      try {
        const sheet = context.getActiveSheet();
        if (!sheet.isEditing()) {
          // Iterating all selected cells (multiple selections, rows and columns)
          let maxRows = 0;
          sheet.suspendPaint();

          sheet.getSelections().forEach((sel, i) => {
            if (maxRows < sel.row + sel.rowCount) {
              maxRows = sel.row + sel.rowCount;
            }
            for (let r = sel.row; r < sel.row + sel.rowCount; r++) {
              for (let c = sel.col; c < sel.col + sel.colCount; c++) {
                sheet.getCell(r, c).clear(GC.Spread.Sheets.StorageType.data);
                setTimeout(() => this.setDefaultValuesForDataTypeCell(r, c));
              }
            }
          });

          sheet.resumePaint();
        }
      } catch (err) {}
    });
    commandManager.setShortcutKey('deleteCell', GC.Spread.Commands.Key.del);
  }

  /**
   * Add validations to the specified row, then disables dimensions that won't be used
   * To execute after SpreadJS events being handled
   */
  private updateValidationsAndFieldsAfterEvent(row: number) {
    addValidationToRow(this.sheet, this.datatableTypes, row);
    disableEmptyDimensionsCells(
      this.sheet,
      this.interpolation,
      this.dimensionDefinition,
      row,
      this.sheet.getCell(row, DIMENSION_COL_POS).value()
    );
  }

  private onClipboardPastedCallback(info) {
    const { row, rowCount, col, colCount } = info.cellRange;

    for (let r = row; r < rowCount + row; r++) {
      // Store interpolation values before they get reset
      const interpolationValues = {
        dim1: this.sheet.getCell(r, DIM1_GROUP.startCol + INTERPOLATION_COL_POS).value(),
        dim2: this.sheet.getCell(r, DIM2_GROUP.startCol + INTERPOLATION_COL_POS).value(),
        dim3: this.sheet.getCell(r, DIM3_GROUP.startCol + INTERPOLATION_COL_POS).value()
      };

      this.updateValidationsAndFieldsAfterEvent(r);

      // Restore interpolation values if they were pasted (not null/undefined)
      if (col <= DIM1_GROUP.startCol + INTERPOLATION_COL_POS && DIM1_GROUP.startCol + INTERPOLATION_COL_POS <= col + colCount) {
        if (interpolationValues.dim1 !== null && interpolationValues.dim1 !== undefined) {
          this.sheet.getCell(r, DIM1_GROUP.startCol + INTERPOLATION_COL_POS).value(interpolationValues.dim1);
        }
      }
      if (col <= DIM2_GROUP.startCol + INTERPOLATION_COL_POS && DIM2_GROUP.startCol + INTERPOLATION_COL_POS <= col + colCount) {
        if (interpolationValues.dim2 !== null && interpolationValues.dim2 !== undefined) {
          this.sheet.getCell(r, DIM2_GROUP.startCol + INTERPOLATION_COL_POS).value(interpolationValues.dim2);
        }
      }
      if (col <= DIM3_GROUP.startCol + INTERPOLATION_COL_POS && DIM3_GROUP.startCol + INTERPOLATION_COL_POS <= col + colCount) {
        if (interpolationValues.dim3 !== null && interpolationValues.dim3 !== undefined) {
          this.sheet.getCell(r, DIM3_GROUP.startCol + INTERPOLATION_COL_POS).value(interpolationValues.dim3);
        }
      }

      if (col <= DIM1_GROUP.startCol + DATATYPE_COL_POS && DIM1_GROUP.startCol + DATATYPE_COL_POS <= col + colCount) {
        this.setDefaultValuesForDataTypeCell(r, DIM1_GROUP.startCol + DATATYPE_COL_POS);
      }
      if (col <= DIM2_GROUP.startCol + DATATYPE_COL_POS && DIM2_GROUP.startCol + DATATYPE_COL_POS <= col + colCount) {
        this.setDefaultValuesForDataTypeCell(r, DIM2_GROUP.startCol + DATATYPE_COL_POS);
      }
      if (col <= DIM3_GROUP.startCol + DATATYPE_COL_POS && DIM3_GROUP.startCol + DATATYPE_COL_POS <= col + colCount) {
        this.setDefaultValuesForDataTypeCell(r, DIM3_GROUP.startCol + DATATYPE_COL_POS);
      }

      // Only call onNumberOfDimensionsChanged if we're not pasting into interpolation columns
      // This prevents overwriting the restored interpolation values
      const isPastingIntoInterpolationColumns = 
        (col <= DIM1_GROUP.startCol + INTERPOLATION_COL_POS && DIM1_GROUP.startCol + INTERPOLATION_COL_POS <= col + colCount) ||
        (col <= DIM2_GROUP.startCol + INTERPOLATION_COL_POS && DIM2_GROUP.startCol + INTERPOLATION_COL_POS <= col + colCount) ||
        (col <= DIM3_GROUP.startCol + INTERPOLATION_COL_POS && DIM3_GROUP.startCol + INTERPOLATION_COL_POS <= col + colCount);
      
      if (!isPastingIntoInterpolationColumns) {
        this.onNumberOfDimensionsChanged(r);
      }
      
      setTimeout(() => this.sheet.autoFitColumn(r));
    }
  }

  private setDataTypeFromDefinition(sheet: GC.Spread.Sheets.Worksheet, dimensionDefinition: DimensionDefinitionItem[], row: any, col: any) {
    const updates = this.getChangesToRows().updatedLookupTables;
    const currentDefinitionValue: string = sheet.getCell(row, col).value() || '';
    const valueToSetup = dimensionDefinition.find(o => o.name.toLowerCase() === currentDefinitionValue.toString().toLowerCase());
    if (valueToSetup && valueToSetup.type) {
      sheet.suspendEvent();
      sheet.getCell(row, col + DATATYPE_COL_POS).value(valueToSetup.type);
      updates.forEach(update => {
        if (update.originalItem && update.row === row) {
          let oldDefinitionValue;
          switch (col) {
            case DIM1_GROUP.startCol + DEFINITION_COL_POS:
              oldDefinitionValue = update.originalItem.DimMnemonic1;
              break;
            case DIM2_GROUP.startCol + DEFINITION_COL_POS:
              oldDefinitionValue = update.originalItem.DimMnemonic2;
              break;
            case DIM3_GROUP.startCol + DEFINITION_COL_POS:
              oldDefinitionValue = update.originalItem.DimMnemonic3;
              break;
          }
          this.changes.push({
            row,
            col,
            oldDefinitionValue,
            oldDataType: oldDefinitionValue
              ? this.dimensionDefinition.find(o => o.name === oldDefinitionValue)
                ? this.dimensionDefinition.find(o => o.name === oldDefinitionValue).type
                : null
              : null,
          });
        }
      });
      sheet.resumeEvent();
    }
  }

  private setDefaultValuesForDataTypeCell(row: any, col: any) {
    switch (col) {
      case DIM1_GROUP.startCol + DEFINITION_COL_POS:
      case DIM1_GROUP.startCol + DATATYPE_COL_POS:
        this.setDataTypeFromDefinition(this.sheet, this.dimensionDefinition, row, DIM1_GROUP.startCol + DEFINITION_COL_POS);
        break;
      case DIM2_GROUP.startCol + DEFINITION_COL_POS:
      case DIM2_GROUP.startCol + DATATYPE_COL_POS:
        this.setDataTypeFromDefinition(this.sheet, this.dimensionDefinition, row, DIM2_GROUP.startCol + DEFINITION_COL_POS);
        break;
      case DIM3_GROUP.startCol + DEFINITION_COL_POS:
      case DIM3_GROUP.startCol + DATATYPE_COL_POS:
        this.setDataTypeFromDefinition(this.sheet, this.dimensionDefinition, row, DIM3_GROUP.startCol + DEFINITION_COL_POS);
        break;
    }

    addValidationToRow(this.sheet, this.datatableTypes, row);
  }

  /**
   * Handles the Save button in the front end
   */
  saveData() {
    const { newLookupTables, updatedLookupTables, deletedLookupTables } = this.getChangesToRows();

    this.isSaving = true;
    this.notifications.hideToasts();

    const calls: Observable<any>[] = [];
    if (newLookupTables.length > 0) {
      calls.push(this.tableSetupService.addLookupTableSetup(newLookupTables));
    }
    if (updatedLookupTables.length > 0) {
      calls.push(this.tableSetupService.updateLookupTableSetup(updatedLookupTables));
    }
    if (deletedLookupTables.length > 0) {
      calls.push(this.tableSetupService.deleteLookupTableSetup(deletedLookupTables));
    }

    if (calls.length > 0) {
      forkJoin(calls)
        .toPromise()
        .then(data => {
          this.onSaveSuccess();
        });
    }
  }

  private getChangesToRows() {
    const newLookupTables: any[] = [];
    const updatedLookupTables: any[] = [];
    const deletedLookupTables: any[] = [];

    const changedRows: any[] = [...this.sheet.getInsertRows(), ...this.sheet.getDirtyRows(), ...this.sheet.getDeletedRows()]
      // Sorted by:
      // -- ascending order depending of the row number
      .sort((a, b) => a.row - b.row);

    changedRows.forEach((row, i) => {
      if ((row.item && !row.item.Id && row.item.Mnemonic) || (!row.originalItem && !!row.item.Mnemonic)) {
        newLookupTables.push(row.item);
      } else if (row.originalItem && row.item && row.originalItem.Mnemonic && !this.isEmptyRow(row.item)) {
        updatedLookupTables.push(row);
      } else if (row.originalItem && (!row.item || this.isEmptyRow(row.item))) {
        if (row.originalItem.Id) this.willCauseDataLoss = true;
        delete row.item;
        deletedLookupTables.push(row);
      } else {
        // 'Ignored row due to missing mnemonic'
      }
    });

    this.zone.run(_ => {
      this.dirtyRows = this.isLoading ? [] : [...newLookupTables, ...updatedLookupTables, ...deletedLookupTables];
    });

    return { newLookupTables, updatedLookupTables, deletedLookupTables };
  }

  private onSaveSuccess() {
    this.notifications.showToast(ToastLevel.Success,this.toast, 'View data', 'lookup-data');

    this.getTableSetup();
    this.sheet.clearPendingChanges();

    this.changes = [];
    this.dirtyRows = [];
    this.lookupTableSetup.map(x => {
      if (x.new && Object.keys(x).length > 1) {
        delete x['new'];
      }
    });
    this.addBottomRowsIfNeeded();
    this.isSaving = false;
    this.checkSaveEnabled();
  }

  public async checkSaveEnabled() {
    if (this.userRoleCanEdit) {
      if (this.changeTimeout) {
        clearTimeout(this.changeTimeout);
        this.changeTimeout = null;
      }

      this.changeTimeout = setTimeout(() => {
        // Handling errors when switching tabs quickly
        try {
          const listOfInsertedRowsWithData = this.sheet
            .getInsertRows()
            .filter(info => info.item && Object.values(info.item).some(value => !_isEmpty(value)));
          const numberOfChangedrows =
            this.sheet.getDirtyRows().length + this.sheet.getDeletedRows().length + listOfInsertedRowsWithData.length;

          const newCondition = numberOfChangedrows > 0 && this.isValidTable();

          if (newCondition !== this._saveEnabled) {
            this._saveEnabled = newCondition;
            this.menu.emitResize();
            this.menu.selectedMenuItemCanDeactivate(!newCondition);
          }
        } catch {}

        return this._saveEnabled;
      }, 100);
    }
  }

  /**
   * If all table keys are null, undefined or empty string
   */
  public isEmptyRow(table: ILookupSetup) {
    if (isEmpty(table)) {
      return true;
    }

    const tableCopy: ILookupSetup = clone(table);
    delete tableCopy.Id;
    delete tableCopy.Update;

    const isEmptyRow = Object.values(tableCopy).every(value => isEmpty(value));
    return isEmptyRow;
  }

  private isValidRow(row: number) {
    let valid = true;
    for (let col = 0; valid && col < this.sheet.getColumnCount(); col++) {
      valid = valid && this.sheet.isValid(row, col, this.sheet.getValue(row, col));
    }

    return valid;
  }

  /**
   * Validates the table status and returns if table is valid. Also triggers showDiscardToast()
   */
  public isValidTable(): boolean {
    this.sheet.suspendPaint();
    let valid = true;
    for (let row = 0; valid && row < this.sheet.getRowCount(); row++) {
      valid = valid && this.isValidRow(row);
    }

    const allEmpty = this.lookupTableSetup.every(t => this.isEmptyRow(t));
    if (allEmpty) {
      valid = true;
    }

    if (valid && !this.isSaving && !this.isLoading) {
      const { updatedLookupTables, deletedLookupTables } = this.getChangesToRows();

      // console.log({ updatedLookupTables, deletedLookupTables });

      const changesThatTriggerLosses = updatedLookupTables.filter(
        table =>
          table.item &&
          table.item.Id &&
          (table.item.DataType1 !== table.originalItem.DataType1 ||
            table.item.DataType2 !== table.originalItem.DataType2 ||
            table.item.DataType3 !== table.originalItem.DataType3 ||
            table.item.Mnemonic !== table.originalItem.Mnemonic ||
            table.item.Dimensions !== table.originalItem.Dimensions)
      );

      if (changesThatTriggerLosses.length > 0 || this.willCauseDataLoss) this.showDiscardToast();
    }

    this.menu.dialogSaveButton = { page: 'lookup-setup', valid };
    this.isSheetValid = valid;
    this.sheet.resumePaint();

    return valid;
  }

  private onNumberOfDimensionsChanged(row, amount = null) {
    // Add "EXACT" Interpolation as default
    // Forcing numeric evaluation to make sure row exists and is equal or higher than 0

    if (amount === null) {
      amount = this.sheet.getCell(row, DIMENSION_COL_POS).value();
    }

    if (row >= 0 && amount) {
      this.sheet.getCell(row, DIM3_GROUP.startCol + INTERPOLATION_COL_POS).value(DEFAULT_FIELDS.INTERPOLATION);
      this.sheet.getCell(row, DIM2_GROUP.startCol + INTERPOLATION_COL_POS).value(DEFAULT_FIELDS.INTERPOLATION);
      this.sheet.getCell(row, DIM1_GROUP.startCol + INTERPOLATION_COL_POS).value(DEFAULT_FIELDS.INTERPOLATION);

      if (this.lookupTableSetup[row]) {
        switch (this.sheet.getCell(row, DIMENSION_COL_POS).value()) {
          case 1:
            if (!this.lookupTableSetup[row].DimMnemonic2 || !this.lookupTableSetup[row].Id) {
              this.sheet.getCell(row, DIM2_GROUP.startCol + INTERPOLATION_COL_POS).clear(GC.Spread.Sheets.StorageType.data);
            }
          /* falls through */
          case 2:
            if (!this.lookupTableSetup[row].DimMnemonic3 || !this.lookupTableSetup[row].Id) {
              this.sheet.getCell(row, DIM3_GROUP.startCol + INTERPOLATION_COL_POS).clear(GC.Spread.Sheets.StorageType.data);
            }
            break;
          case 3:
            // Adding empty case here so 3 does not fall into "default"
            break;
          default:
            this.sheet.getCell(row, DIM1_GROUP.startCol + INTERPOLATION_COL_POS).clear(GC.Spread.Sheets.StorageType.data);
            this.sheet.getCell(row, DIM2_GROUP.startCol + INTERPOLATION_COL_POS).clear(GC.Spread.Sheets.StorageType.data);
            this.sheet.getCell(row, DIM3_GROUP.startCol + INTERPOLATION_COL_POS).clear(GC.Spread.Sheets.StorageType.data);
        }
      }
    }

    // Disable cells that are not available
    disableEmptyDimensionsCells(this.sheet, this.interpolation, this.dimensionDefinition, row, amount);
  }

  async canDeactivate(): Promise<boolean> {
    // const message =
    //   'You have unsaved changes on this page. Please click Cancel to return to the page or continue to discard changes';
    return this.menu.canDeactivatePopup();
  }

  private showDiscardToast() {
    const body = `You have made changes that may cause data to be lost. Press 'Save' to confirm, or 'Discard'. `;
    // Rename to discardToast
    const discardToast: Toast = { title: '', message: body} as Toast;
    this.notifications.hideToasts();
    this.notifications.showToast(ToastLevel.Warning, discardToast, 'Discard', 'lookup-setup-discard');
  }

  /**
   * Discard all changes from sheet by resetting the Spread object and pulling the tableSetup data from DB
   */
  private discardChanges() {
    this.sheet.reset();
    this.getTableSetup();
  }
}
